/**
 * AgentEventProvider runs the per-conversation event hooks once and
 * exposes their results via React context, eliminating prop-drilling
 * of cross-cutting agent state through Thread/Composer.
 *
 * Mounted inside the per-conversation runtime, above Thread:
 *
 *   <AssistantRuntimeProvider runtime={runtime}>
 *     <AgentEventProvider agent={agent} runtime={runtime} api={api}
 *                          conversationId={conversationId} baseUrl={...}>
 *       <Thread ... />
 *     </AgentEventProvider>
 *   </AssistantRuntimeProvider>
 *
 * Thread (and any descendant) reads the values via `useAgentEvents()`,
 * which returns safe defaults when no provider is in scope (so
 * read-only viewers can render Thread without one if desired).
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { ConversationStatus } from "~/components/assistant/types";
import { McpAppEventsProvider, SubagentEventsProvider } from "./AgentContext";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import type { ContextBuffer } from "./useContextBuffer";
import { useContextBuffer } from "./useContextBuffer";
import { useHistoryLoad } from "./useHistoryLoad";
import { useMcpAppEvents } from "./useMcpAppEvents";
import { useMcpAppHydration } from "./useMcpAppHydration";
import { useStreamingTurnIds } from "./useMessageFeedback";
import { useRemoteRun } from "./useRemoteRun";
import { useRunError } from "./useRunError";
import { useSubagentEvents } from "./useSubagentEvents";

export interface AgentEventState {
  streamingTurnIds: Map<string, string>;
  runError: string | null;
  clearError: () => void;
  contextBuffer: ContextBuffer | null;
  isLoadingHistory: boolean;
  historyHasMessages: boolean;
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

const EMPTY_TURN_IDS: Map<string, string> = new Map();
const noop = () => {};

const DEFAULT_AGENT_EVENT_STATE: AgentEventState = {
  streamingTurnIds: EMPTY_TURN_IDS,
  runError: null,
  clearError: noop,
  contextBuffer: null,
  isLoadingHistory: false,
  historyHasMessages: false,
  isRunningRemotely: false,
  isCancelling: false,
  onCancelRun: noop,
};

const AgentEventContext = createContext<AgentEventState | null>(null);

/** Returns event state from the nearest AgentEventProvider, or safe defaults. */
export function useAgentEvents(): AgentEventState {
  return useContext(AgentEventContext) ?? DEFAULT_AGENT_EVENT_STATE;
}

interface AgentEventProviderProps {
  agent: ReplayHttpAgent;
  runtime: AssistantRuntime;
  api: AssistantApi;
  conversationId: string;
  baseUrl: string;
  /** Pre-converted messages from /connect — skips an extra fetch. */
  initialMessages?: ThreadMessageLike[] | null;
  /** Conversation status from /connect; drives replay attach on mount. */
  status: ConversationStatus;
  children: ReactNode;
}

export function AgentEventProvider({
  agent,
  runtime,
  api,
  conversationId,
  baseUrl,
  initialMessages,
  status,
  children,
}: AgentEventProviderProps) {
  const { error: runError, clearError } = useRunError(agent);
  const streamingTurnIds = useStreamingTurnIds(agent, runtime);
  const contextBuffer = useContextBuffer(agent);
  const subagentEvents = useSubagentEvents(agent);
  const liveMcpAppEvents = useMcpAppEvents(agent);

  const { isLoadingHistory, historyHasMessages, reloadHistory } = useHistoryLoad({
    agent,
    runtime,
    baseUrl,
    conversationId,
    initialMessages,
  });

  const hydratedMcpAppEntries = useMcpAppHydration(initialMessages, isLoadingHistory);

  const mcpAppEvents = useMemo(() => {
    if (liveMcpAppEvents.size === 0) return hydratedMcpAppEntries;
    if (hydratedMcpAppEntries.size === 0) return liveMcpAppEvents;
    return new Map([...hydratedMcpAppEntries, ...liveMcpAppEvents]);
  }, [liveMcpAppEvents, hydratedMcpAppEntries]);

  const { isRunningRemotely, isCancelling, onCancelRun } = useRemoteRun({
    agent,
    runtime,
    api,
    conversationId,
    status,
    initialMessages,
    clearError,
    onHistoryReload: reloadHistory,
  });

  const value = useMemo<AgentEventState>(
    () => ({
      streamingTurnIds,
      runError,
      clearError,
      contextBuffer,
      isLoadingHistory,
      historyHasMessages,
      isRunningRemotely,
      isCancelling,
      onCancelRun,
    }),
    [
      streamingTurnIds,
      runError,
      clearError,
      contextBuffer,
      isLoadingHistory,
      historyHasMessages,
      isRunningRemotely,
      isCancelling,
      onCancelRun,
    ]
  );

  return (
    <AgentEventContext.Provider value={value}>
      <SubagentEventsProvider value={subagentEvents}>
        <McpAppEventsProvider value={mcpAppEvents}>{children}</McpAppEventsProvider>
      </SubagentEventsProvider>
    </AgentEventContext.Provider>
  );
}
